import React, { useEffect, useRef, useState } from 'react';
import {
  Drawer,
  Message,
  Modal,
  Skeleton,
  Tooltip,
} from '@arco-design/web-react';
import {
  IconCopy,
  IconDelete,
  IconDown,
  IconHistory,
  IconMinus,
  IconPlus,
  IconRefresh,
  IconRight,
} from '@arco-design/web-react/icon';
import {
  useDocumentVisibility,
  useInterval,
  useLockFn,
  useMemoizedFn,
  useUnmount,
  useUpdateEffect,
} from 'ahooks';
import MarkdownContent from '../MarkdownContent';
import {
  apiCreateAssistantChat,
  apiDeleteMyAssistantChat,
  apiGetMyAssistantChatDetail,
  apiListMyAssistantChats,
  apiStreamAssistantChat,
} from '@/api/assistant';
import {
  IAssistantConfig,
  IAssistantConversation,
  IAssistantMessage,
  IAssistantProcessStep,
} from '@/api/typings/assistant';
import {
  ASSISTANT_CHAT_BUSY_MESSAGE,
  ASSISTANT_CHAT_BUSY_REASON,
  ASSISTANT_MY_CHAT_PAGE_SIZE,
  AssistantMessageRole,
} from '@/constants/business/assistant';
import {
  getAssistantChatSize,
  setAssistantChatSize,
} from '@/utils/assistantStorage';
import styles from './index.module.less';

interface IProps {
  config: IAssistantConfig;
  visible: boolean;
  conversationId?: string;
  onConversationChange: (id?: string) => void;
  onClose: () => void;
}

const DRAFT_STREAM_KEY = '__draft__';
const HISTORY_LOAD_MORE_OFFSET = 40;
const CHAT_MIN_WIDTH = 400;
const CHAT_MIN_HEIGHT = 580;
const CHAT_SIDE_MARGIN = 48;
const CHAT_TOP_MARGIN = 72;
const WAIT_LABEL = '正在准备回复';

type ReplyTimerStatus = 'running' | 'stopped' | 'failed';

interface IReplyTimer {
  waitStartedAt: number;
  thinkStartedAt?: number;
  waitSec?: number;
  thinkSec?: number;
  contentStarted?: boolean;
  status: ReplyTimerStatus;
}

const elapsedSec = (startedAt: number, endAt: number) =>
  Math.max(0, Math.floor((endAt - startedAt) / 1000));

const formatClock = (sec: number) => {
  if (sec < 60) {
    return `${sec}s`;
  }
  const minutes = Math.floor(sec / 60);
  const rest = sec % 60;
  return `${minutes}分${String(rest).padStart(2, '0')}秒`;
};

const formatDuration = (sec: number) => {
  if (sec < 60) {
    return `用时 ${sec} 秒`;
  }
  const minutes = Math.floor(sec / 60);
  const rest = sec % 60;
  return `用时 ${minutes}分${String(rest).padStart(2, '0')}秒`;
};

const withClock = (label: string, sec: number) =>
  `${label} ${formatClock(sec)}`;

const withDuration = (label: string, sec: number) =>
  `${label}（${formatDuration(sec)}）`;

type ResizeDir = 'n' | 'w' | 'nw';

const clampChatSize = (width: number, height: number) => ({
  width: Math.min(
    window.innerWidth - CHAT_SIDE_MARGIN,
    Math.max(CHAT_MIN_WIDTH, width)
  ),
  height: Math.min(
    window.innerHeight - CHAT_TOP_MARGIN,
    Math.max(CHAT_MIN_HEIGHT, height)
  ),
});

const pickConversationNo = (
  preferred: string | undefined,
  list: IAssistantConversation[]
) => {
  if (preferred) {
    return preferred;
  }
  if (list.length === 0) {
    return undefined;
  }
  return [...list].sort(
    (left, right) => Number(right.lastMessageAt) - Number(left.lastMessageAt)
  )[0].conversationNo;
};

const ChatWindow = ({
  config,
  visible,
  conversationId,
  onConversationChange,
  onClose,
}: IProps) => {
  const documentVisibility = useDocumentVisibility();
  const [messages, setMessages] = useState<IAssistantMessage[]>([]);
  const [input, setInput] = useState('');
  const [streamingMap, setStreamingMap] = useState<Record<string, boolean>>({});
  const [ready, setReady] = useState(false);
  const [history, setHistory] = useState<IAssistantConversation[]>([]);
  const [historyHasMore, setHistoryHasMore] = useState(true);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [thinkOpenMap, setThinkOpenMap] = useState<Record<string, boolean>>({});
  const [replyTimers, setReplyTimers] = useState<Record<string, IReplyTimer>>(
    {}
  );
  const [clock, setClock] = useState(() => Date.now());
  const streamMapRef = useRef<Map<string, AbortController>>(new Map());
  const streamingMapRef = useRef(streamingMap);
  const replyTimersRef = useRef(replyTimers);
  const messagesCacheRef = useRef<Record<string, IAssistantMessage[]>>({});
  const loadedIdRef = useRef<string>();
  const detailRequestIdRef = useRef<string>();
  const historyPageRef = useRef(1);
  const skipAutoScrollRef = useRef(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);
  const inputMaxHeight = 126;
  const [size, setSize] = useState(
    () =>
      getAssistantChatSize() || {
        width: CHAT_MIN_WIDTH,
        height: CHAT_MIN_HEIGHT,
      }
  );
  const sizeRef = useRef(size);
  const resizeRef = useRef<{
    dir: ResizeDir;
    startX: number;
    startY: number;
    originW: number;
    originH: number;
  }>();
  sizeRef.current = size;
  streamingMapRef.current = streamingMap;
  replyTimersRef.current = replyTimers;
  const sending = Boolean(streamingMap[conversationId || DRAFT_STREAM_KEY]);
  const hasStreaming = Object.keys(streamingMap).length > 0;

  useInterval(
    () => {
      setClock(Date.now());
    },
    hasStreaming ? 1000 : undefined
  );

  const resizeInput = useMemoizedFn(() => {
    const el = inputRef.current;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, inputMaxHeight)}px`;
  });

  const scrollToBottom = useMemoizedFn(() => {
    const box = listRef.current;
    if (box) {
      box.scrollTop = box.scrollHeight;
    }
  });

  const commitMessages = useMemoizedFn(
    (
      id: string,
      updater: (prev: IAssistantMessage[]) => IAssistantMessage[]
    ) => {
      const prev = messagesCacheRef.current[id] || [];
      const next = updater(prev);
      messagesCacheRef.current[id] = next;
      if (loadedIdRef.current === id) {
        setMessages(next);
      }
    }
  );

  const markStreaming = useMemoizedFn((id: string, active: boolean) => {
    if (active) {
      streamingMapRef.current = { ...streamingMapRef.current, [id]: true };
    } else {
      const next = { ...streamingMapRef.current };
      delete next[id];
      streamingMapRef.current = next;
    }
    setStreamingMap(streamingMapRef.current);
  });

  const patchReplyTimer = useMemoizedFn(
    (id: string, updater: (prev: IReplyTimer) => IReplyTimer) => {
      const current = replyTimersRef.current[id];
      if (!current) {
        return;
      }
      const next = {
        ...replyTimersRef.current,
        [id]: updater(current),
      };
      replyTimersRef.current = next;
      setReplyTimers(next);
    }
  );

  const startReplyTimer = useMemoizedFn((id: string) => {
    const next = {
      ...replyTimersRef.current,
      [id]: {
        waitStartedAt: Date.now(),
        status: 'running' as const,
      },
    };
    replyTimersRef.current = next;
    setReplyTimers(next);
    setClock(Date.now());
  });

  const markThinkStart = useMemoizedFn((id: string) => {
    patchReplyTimer(id, (prev) => {
      if (prev.status !== 'running' || prev.thinkStartedAt) {
        return prev;
      }
      const now = Date.now();
      return {
        ...prev,
        thinkStartedAt: now,
        waitSec: prev.waitSec ?? elapsedSec(prev.waitStartedAt, now),
      };
    });
  });

  const markContentStart = useMemoizedFn((id: string) => {
    patchReplyTimer(id, (prev) => {
      if (prev.status !== 'running' || prev.contentStarted) {
        return prev;
      }
      const now = Date.now();
      return {
        ...prev,
        contentStarted: true,
        waitSec: prev.waitSec ?? elapsedSec(prev.waitStartedAt, now),
        thinkSec: prev.thinkStartedAt
          ? prev.thinkSec ?? elapsedSec(prev.thinkStartedAt, now)
          : prev.thinkSec,
      };
    });
  });

  const finishReplyTimer = useMemoizedFn(
    (id: string, status: ReplyTimerStatus) => {
      patchReplyTimer(id, (prev) => {
        if (prev.status !== 'running') {
          return prev;
        }
        const now = Date.now();
        return {
          ...prev,
          status,
          waitSec: prev.waitSec ?? elapsedSec(prev.waitStartedAt, now),
          thinkSec: prev.thinkStartedAt
            ? prev.thinkSec ?? elapsedSec(prev.thinkStartedAt, now)
            : prev.thinkSec,
        };
      });
    }
  );

  const stopStream = useMemoizedFn((id: string) => {
    const lastAssistant = [...(messagesCacheRef.current[id] || [])]
      .reverse()
      .find((item) => item.role === AssistantMessageRole.ASSISTANT);
    if (lastAssistant) {
      finishReplyTimer(lastAssistant.id, 'stopped');
    }
    streamMapRef.current.get(id)?.abort();
    streamMapRef.current.delete(id);
    markStreaming(id, false);
  });

  const loadHistory = useMemoizedFn(async () => {
    const data = await apiListMyAssistantChats({
      page: 1,
      size: ASSISTANT_MY_CHAT_PAGE_SIZE,
    });
    const list = data.list || [];
    historyPageRef.current = 1;
    setHistoryHasMore(list.length >= ASSISTANT_MY_CHAT_PAGE_SIZE);
    setHistory(list);
    return list;
  });

  const loadMoreHistory = useLockFn(async () => {
    if (!historyHasMore || historyLoadingMore) {
      return;
    }
    setHistoryLoadingMore(true);
    try {
      const page = historyPageRef.current + 1;
      const data = await apiListMyAssistantChats({
        page,
        size: ASSISTANT_MY_CHAT_PAGE_SIZE,
      });
      const list = data.list || [];
      historyPageRef.current = page;
      setHistoryHasMore(list.length >= ASSISTANT_MY_CHAT_PAGE_SIZE);
      setHistory((prev) => {
        const exist = new Set(prev.map((item) => item.conversationNo));
        return [
          ...prev,
          ...list.filter((item) => !exist.has(item.conversationNo)),
        ];
      });
    } finally {
      setHistoryLoadingMore(false);
    }
  });

  const loadDetail = useMemoizedFn(async (id: string) => {
    detailRequestIdRef.current = id;
    try {
      const detail = await apiGetMyAssistantChatDetail({
        conversationNo: id,
      });
      messagesCacheRef.current[id] = detail.messages;
      if (detailRequestIdRef.current !== id) {
        return;
      }
      loadedIdRef.current = id;
      setMessages(detail.messages);
    } catch {
      if (detailRequestIdRef.current !== id) {
        return;
      }
      loadedIdRef.current = undefined;
      delete messagesCacheRef.current[id];
      onConversationChange(undefined);
      setMessages([]);
    }
  });

  const enterDraft = useMemoizedFn(() => {
    detailRequestIdRef.current = undefined;
    loadedIdRef.current = undefined;
    setMessages([]);
    setThinkOpenMap({});
    onConversationChange(undefined);
  });

  const restoreOrLoad = useMemoizedFn((id: string) => {
    if (streamingMapRef.current[id] && messagesCacheRef.current[id]) {
      loadedIdRef.current = id;
      setMessages(messagesCacheRef.current[id]);
      return Promise.resolve();
    }
    return loadDetail(id);
  });

  const boot = useMemoizedFn(async () => {
    try {
      const list = await loadHistory();
      const nextId = pickConversationNo(conversationId, list);
      if (nextId) {
        if (nextId !== conversationId) {
          onConversationChange(nextId);
        }
        await restoreOrLoad(nextId);
        return;
      }
      loadedIdRef.current = undefined;
      if (conversationId) {
        onConversationChange(undefined);
      }
      setMessages([]);
    } finally {
      setReady(true);
    }
  });

  const refreshVisibleData = useMemoizedFn(async (keepScroll?: boolean) => {
    if (!ready) {
      return;
    }
    await loadHistory();
    if (!conversationId) {
      return;
    }
    if (streamingMapRef.current[conversationId]) {
      return;
    }
    if (keepScroll) {
      skipAutoScrollRef.current = true;
    }
    await loadDetail(conversationId);
  });

  useEffect(() => {
    boot();
  }, [boot]);

  useEffect(() => {
    if (!ready) {
      return;
    }
    if (!conversationId) {
      if (loadedIdRef.current) {
        return;
      }
      setMessages([]);
      return;
    }
    if (loadedIdRef.current === conversationId) {
      return;
    }
    restoreOrLoad(conversationId);
  }, [conversationId, restoreOrLoad, ready]);

  useUpdateEffect(() => {
    if (!visible) {
      return;
    }
    refreshVisibleData();
  }, [visible]);

  useUpdateEffect(() => {
    if (!visible || documentVisibility !== 'visible') {
      return;
    }
    refreshVisibleData(true);
  }, [documentVisibility]);

  useEffect(() => {
    if (!ready) {
      return undefined;
    }
    if (skipAutoScrollRef.current) {
      skipAutoScrollRef.current = false;
      return undefined;
    }
    scrollToBottom();
    const frame = window.requestAnimationFrame(scrollToBottom);
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [ready, messages, visible, scrollToBottom]);

  useEffect(() => {
    resizeInput();
  }, [input, resizeInput]);

  useUnmount(() => {
    streamMapRef.current.forEach((controller) => controller.abort());
    streamMapRef.current.clear();
  });

  const handleNewChat = useMemoizedFn(() => {
    setHistoryVisible(false);
    if (!ready || (!conversationId && messages.length === 0)) {
      return;
    }
    enterDraft();
  });

  const handleSwitch = useMemoizedFn((id: string) => {
    if (streamingMapRef.current[id] && messagesCacheRef.current[id]) {
      loadedIdRef.current = id;
      setMessages(messagesCacheRef.current[id]);
    } else {
      setMessages([]);
    }
    onConversationChange(id);
    setHistoryVisible(false);
  });

  const handleDelete = useLockFn(async (id: string) => {
    stopStream(id);
    delete messagesCacheRef.current[id];
    await apiDeleteMyAssistantChat({ conversationNo: id });
    Message.success('已删除对话');
    if (id === conversationId) {
      enterDraft();
    }
    await loadHistory();
  });

  const confirmDelete = useMemoizedFn((id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    Modal.confirm({
      title: '确定删除这条对话吗？',
      onOk: () => handleDelete(id),
    });
  });

  const sendQuestion = useMemoizedFn(
    async (text: string, regenerate = false) => {
      const content = text.trim();
      const streamKey = conversationId || DRAFT_STREAM_KEY;
      if (!content || streamingMapRef.current[streamKey]) {
        return;
      }
      const controller = new AbortController();
      streamMapRef.current.set(streamKey, controller);
      markStreaming(streamKey, true);
      const userMessage: IAssistantMessage = {
        id: `tmp-${Date.now()}`,
        role: AssistantMessageRole.USER,
        content,
      };
      const assistantId = `tmp-a-${Date.now()}`;
      const assistantMessage: IAssistantMessage = {
        id: assistantId,
        role: AssistantMessageRole.ASSISTANT,
        content: '',
      };
      const appendOptimistic = (prev: IAssistantMessage[]) => {
        const base =
          regenerate &&
          prev[prev.length - 1]?.role === AssistantMessageRole.ASSISTANT
            ? prev.slice(0, -1)
            : prev;
        return regenerate
          ? [...base, assistantMessage]
          : [...base, userMessage, assistantMessage];
      };
      if (conversationId) {
        commitMessages(conversationId, appendOptimistic);
      } else {
        setMessages((prev) => {
          const next = appendOptimistic(prev);
          messagesCacheRef.current[DRAFT_STREAM_KEY] = next;
          return next;
        });
        loadedIdRef.current = DRAFT_STREAM_KEY;
      }
      setInput('');
      setThinkOpenMap((prev) => ({ ...prev, [assistantId]: true }));
      startReplyTimer(assistantId);
      let chatId = conversationId;
      try {
        if (!chatId) {
          const created = await apiCreateAssistantChat();
          if (controller.signal.aborted) {
            return;
          }
          if (!created.conversationNo) {
            throw new Error('创建会话失败');
          }
          const createdNo = created.conversationNo;
          chatId = createdNo;
          messagesCacheRef.current[createdNo] =
            messagesCacheRef.current[DRAFT_STREAM_KEY] || [];
          delete messagesCacheRef.current[DRAFT_STREAM_KEY];
          streamMapRef.current.delete(DRAFT_STREAM_KEY);
          streamMapRef.current.set(createdNo, controller);
          markStreaming(DRAFT_STREAM_KEY, false);
          markStreaming(createdNo, true);
          loadedIdRef.current = createdNo;
          onConversationChange(createdNo);
        }
        if (!chatId) {
          throw new Error('创建会话失败');
        }
        const convNo = chatId;
        const result = await apiStreamAssistantChat({
          conversationId: convNo,
          content,
          signal: controller.signal,
          onProcessSteps: (steps) => {
            if (
              steps.some(
                (step) => step.kind === 'thinking' && Boolean(step.text)
              )
            ) {
              markThinkStart(assistantId);
            }
            commitMessages(convNo, (prev) =>
              prev.map((item) =>
                item.id === assistantId
                  ? { ...item, processSteps: steps }
                  : item
              )
            );
          },
          onChunk: (chunk) => {
            if (chunk) {
              markContentStart(assistantId);
            }
            commitMessages(convNo, (prev) =>
              prev.map((item) =>
                item.id === assistantId ? { ...item, content: chunk } : item
              )
            );
          },
        });
        if (controller.signal.aborted) {
          return;
        }
        if (result.reason === ASSISTANT_CHAT_BUSY_REASON) {
          commitMessages(convNo, (prev) => {
            const withoutAssistant = prev.filter(
              (item) => item.id !== assistantId
            );
            if (regenerate) {
              return withoutAssistant;
            }
            return withoutAssistant.filter(
              (item) => item.id !== userMessage.id
            );
          });
          setThinkOpenMap((prev) => {
            const next = { ...prev };
            delete next[assistantId];
            return next;
          });
          const nextTimers = { ...replyTimersRef.current };
          delete nextTimers[assistantId];
          replyTimersRef.current = nextTimers;
          setReplyTimers(nextTimers);
          if (!regenerate && loadedIdRef.current === convNo) {
            setInput(content);
          }
          Message.warning(result.error || ASSISTANT_CHAT_BUSY_MESSAGE);
          return;
        }
        if (result.error) {
          finishReplyTimer(assistantId, 'failed');
        } else {
          finishReplyTimer(assistantId, 'running');
        }
        commitMessages(convNo, (prev) =>
          prev.map((item) =>
            item.id === assistantId
              ? {
                  ...item,
                  source: result.source,
                  unanswered: result.unanswered,
                }
              : item
          )
        );
        setThinkOpenMap((prev) => ({ ...prev, [assistantId]: false }));
        loadHistory();
      } catch {
        if (!controller.signal.aborted) {
          finishReplyTimer(assistantId, 'failed');
        }
      } finally {
        const activeKey = Array.from(streamMapRef.current.entries()).find(
          ([, item]) => item === controller
        )?.[0];
        if (activeKey) {
          streamMapRef.current.delete(activeKey);
          markStreaming(activeKey, false);
        }
      }
    }
  );

  const handleStop = useMemoizedFn(() => {
    stopStream(conversationId || DRAFT_STREAM_KEY);
  });

  const handleCopy = useMemoizedFn(async (text: string) => {
    await navigator.clipboard.writeText(text);
    Message.success('已复制');
  });

  const handleRegenerate = useMemoizedFn(() => {
    const lastUser = [...messages]
      .reverse()
      .find((item) => item.role === AssistantMessageRole.USER);
    if (!lastUser) {
      return;
    }
    sendQuestion(lastUser.content, true);
  });

  const toggleThink = useMemoizedFn((id: string) => {
    setThinkOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));
  });

  const waitingDetail =
    Boolean(conversationId) && loadedIdRef.current !== conversationId;
  const showEmpty = messages.length === 0 && !waitingDetail;

  const toggleHistory = useMemoizedFn(() => {
    setHistoryVisible((visible) => {
      if (!visible) {
        loadHistory();
      }
      return !visible;
    });
  });

  const onResizeDown = useMemoizedFn(
    (dir: ResizeDir, event: React.PointerEvent<HTMLDivElement>) => {
      const rect = windowRef.current?.getBoundingClientRect();
      resizeRef.current = {
        dir,
        startX: event.clientX,
        startY: event.clientY,
        originW: rect?.width || sizeRef.current.width,
        originH: rect?.height || sizeRef.current.height,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  );

  const onResizeMove = useMemoizedFn(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = resizeRef.current;
      if (!drag) {
        return;
      }
      const dx = drag.startX - event.clientX;
      const dy = drag.startY - event.clientY;
      const next = clampChatSize(
        drag.dir === 'n' ? drag.originW : drag.originW + dx,
        drag.dir === 'w' ? drag.originH : drag.originH + dy
      );
      sizeRef.current = next;
      setSize(next);
    }
  );

  const onResizeUp = useMemoizedFn(() => {
    if (!resizeRef.current) {
      return;
    }
    resizeRef.current = undefined;
    setAssistantChatSize(sizeRef.current);
  });

  return (
    <div
      ref={windowRef}
      className={`${styles.window} ${visible ? '' : styles.windowHidden}`}
      style={{
        width: `min(${size.width}px, calc(100vw - ${CHAT_SIDE_MARGIN}px))`,
        height: `min(${size.height}px, calc(100vh - ${CHAT_TOP_MARGIN}px))`,
      }}
    >
      <div
        className={`${styles.resizeHandle} ${styles.resizeN}`}
        onPointerDown={(event) => onResizeDown('n', event)}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeUp}
        onPointerCancel={onResizeUp}
      />
      <div
        className={`${styles.resizeHandle} ${styles.resizeW}`}
        onPointerDown={(event) => onResizeDown('w', event)}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeUp}
        onPointerCancel={onResizeUp}
      />
      <div
        className={`${styles.resizeHandle} ${styles.resizeNw}`}
        onPointerDown={(event) => onResizeDown('nw', event)}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeUp}
        onPointerCancel={onResizeUp}
      />
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <Tooltip content="新对话">
            <button
              className={styles.iconBtn}
              type="button"
              onClick={handleNewChat}
            >
              <IconPlus fontSize={20} />
            </button>
          </Tooltip>
        </div>
        <div className={styles.title}>智能助手</div>
        <div className={styles.headerRight}>
          <Tooltip content="历史对话">
            <button
              className={`${styles.iconBtn} ${
                historyVisible ? styles.iconBtnActive : ''
              }`}
              type="button"
              onClick={toggleHistory}
            >
              <IconHistory fontSize={20} />
            </button>
          </Tooltip>
          <Tooltip content="收起">
            <button className={styles.iconBtn} type="button" onClick={onClose}>
              <IconMinus fontSize={20} />
            </button>
          </Tooltip>
        </div>
      </header>
      <div className={styles.stage} ref={stageRef}>
        <Drawer
          visible={historyVisible}
          placement="left"
          width={176}
          title="历史对话"
          footer={null}
          closable={false}
          autoFocus={false}
          focusLock={false}
          unmountOnExit
          className={styles.histDrawer}
          wrapClassName={styles.histDrawerWrap}
          getPopupContainer={() => stageRef.current || document.body}
          onCancel={() => setHistoryVisible(false)}
        >
          <div
            className={styles.histList}
            onScroll={(event) => {
              const el = event.currentTarget;
              if (
                el.scrollHeight - el.scrollTop - el.clientHeight <
                HISTORY_LOAD_MORE_OFFSET
              ) {
                loadMoreHistory();
              }
            }}
          >
            {history.length === 0 && (
              <div className={styles.histEmpty}>暂无历史</div>
            )}
            {history.map((item) => (
              <div
                key={item.conversationNo}
                className={`${styles.histItem} ${
                  item.conversationNo === conversationId
                    ? styles.histItemActive
                    : ''
                }`}
                onClick={() => handleSwitch(item.conversationNo)}
              >
                <div className={styles.histTitle}>
                  {item.summary || '新对话'}
                </div>
                <button
                  className={styles.histDel}
                  type="button"
                  onClick={(event) => confirmDelete(item.conversationNo, event)}
                >
                  <IconDelete fontSize={16} />
                </button>
              </div>
            ))}
            {historyLoadingMore && (
              <div className={styles.histEmpty}>加载中...</div>
            )}
          </div>
        </Drawer>
        <div className={styles.body} ref={listRef}>
          {!ready || waitingDetail ? (
            <div className={styles.bootSkeleton}>
              <Skeleton
                animation
                text={{ rows: 3, width: ['58%', '92%', '74%'] }}
              />
              <Skeleton animation text={{ rows: 2, width: ['86%', '64%'] }} />
              <Skeleton animation text={{ rows: 2, width: ['70%', '48%'] }} />
            </div>
          ) : showEmpty ? (
            <div className={styles.empty}>
              <div className={styles.welcome}>你好，我是人事智能助手</div>
              <div className={styles.tryHint}>
                我可解答公司制度相关问题，试试问
              </div>
              <div className={styles.shortcuts}>
                {(config.quickQuestions || []).map((text) => (
                  <button
                    key={text}
                    type="button"
                    className={styles.shortcut}
                    onClick={() => sendQuestion(text)}
                  >
                    {text}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((item, index) => {
              if (item.role === AssistantMessageRole.USER) {
                return (
                  <div key={item.id} className={styles.bubbleMe}>
                    <div className={styles.bubbleText}>{item.content}</div>
                  </div>
                );
              }
              const streaming = sending && index === messages.length - 1;
              const thinkSteps = (item.processSteps || []).filter(
                (
                  step
                ): step is Extract<
                  IAssistantProcessStep,
                  { kind: 'thinking' }
                > => step.kind === 'thinking' && Boolean(step.text)
              );
              const hasThinking = thinkSteps.length > 0;
              const hasContent = Boolean(item.content);
              const thinkOpen = thinkOpenMap[item.id] ?? streaming;
              const timer = replyTimers[item.id];
              const waitSec = timer
                ? timer.waitSec ?? elapsedSec(timer.waitStartedAt, clock)
                : 0;
              const thinkSec = timer?.thinkStartedAt
                ? timer.thinkSec ?? elapsedSec(timer.thinkStartedAt, clock)
                : 0;
              const waitRunning =
                streaming &&
                !hasThinking &&
                !hasContent &&
                timer?.status !== 'stopped' &&
                timer?.status !== 'failed';
              const waitEnded =
                !hasThinking &&
                !hasContent &&
                (timer?.status === 'stopped' || timer?.status === 'failed');
              let waitLabel = withClock(WAIT_LABEL, waitSec);
              if (timer?.status === 'stopped') {
                waitLabel = withDuration('已停止', waitSec);
              } else if (timer?.status === 'failed') {
                waitLabel = withDuration('失败', waitSec);
              }
              let thinkLabel = '已完成思考';
              if (timer?.status === 'stopped') {
                thinkLabel = withDuration('已停止', thinkSec);
              } else if (timer?.status === 'failed') {
                thinkLabel = withDuration('失败', thinkSec);
              } else if (streaming && !hasContent) {
                thinkLabel = withClock('思考中', thinkSec);
              } else if (timer) {
                thinkLabel = withDuration('已完成思考', thinkSec);
              }
              return (
                <div key={item.id} className={styles.botWrap}>
                  {(waitRunning || waitEnded) && (
                    <div className={styles.waitRow}>{waitLabel}</div>
                  )}
                  {hasThinking && (
                    <div className={styles.think}>
                      <button
                        type="button"
                        className={styles.thinkHead}
                        onClick={() => toggleThink(item.id)}
                      >
                        <span>{thinkLabel}</span>
                        {thinkOpen ? (
                          <IconDown fontSize={12} />
                        ) : (
                          <IconRight fontSize={12} />
                        )}
                      </button>
                      {thinkOpen && (
                        <div className={styles.thinkBody}>
                          {thinkSteps.map((step, stepIndex) => (
                            <div key={stepIndex} className={styles.thinkText}>
                              {step.text}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {hasContent && (
                    <div className={styles.bubbleBot}>
                      <MarkdownContent text={item.content} />
                      {item.source && (
                        <div className={styles.bubbleSource}>
                          来源：{item.source}
                        </div>
                      )}
                      <div className={styles.bubbleOps}>
                        <button
                          type="button"
                          className={styles.opBtn}
                          onClick={() => handleCopy(item.content)}
                        >
                          <IconCopy /> 复制
                        </button>
                        {index === messages.length - 1 && !sending && (
                          <button
                            type="button"
                            className={styles.opBtn}
                            onClick={handleRegenerate}
                          >
                            <IconRefresh /> 重新生成
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        <div className={styles.composer}>
          <textarea
            ref={inputRef}
            className={styles.input}
            placeholder="请输入问题"
            value={input}
            rows={1}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                sendQuestion(input);
              }
            }}
          />
          {sending ? (
            <button
              className={styles.stopBtn}
              type="button"
              onClick={handleStop}
              aria-label="停止"
            >
              <span className={styles.stopIcon} />
            </button>
          ) : (
            <button
              className={styles.sendBtn}
              type="button"
              disabled={!ready || !input.trim()}
              onClick={() => sendQuestion(input)}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 12.5V3.5M8 3.5L4 7.5M8 3.5L12 7.5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
